import { useContext, useEffect, useRef, useCallback } from 'react';
import { MainContext } from '../contexts';

interface PageTrackingConfig {
  initialPageName?: string;
  trackingEvents?: {
    pageViewed: string;
    pageLeft: string;
  };
  autoTrack?: boolean;
}

export const usePageTracking = (config: PageTrackingConfig = {}) => {
  const { tracker, loggedUser, company, isMobile, isTablet } = useContext(MainContext);
  const timer = useRef<number>(0);
  const currentPageRef = useRef<string>('');
  
  const device = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';
  
  const defaultEvents = {
    pageViewed: 'page_allie_viewed',
    pageLeft: 'page_allie_left'
  };
  
  const events = config.trackingEvents || defaultEvents;

  const trackPageLeft = useCallback((pageName: string) => {
    if (pageName && timer.current > 0) {
      const duration = Date.now() - timer.current;
      
      tracker?.track(events.pageLeft, {
        page_name: pageName,
        user_id: loggedUser?.id,
        user_name: loggedUser?.name,
        role: loggedUser?.role,
        client: company?.name,
        device: device,
        timestamp: new Date(timer.current).toISOString(),
        duration: duration,
      });
      
      timer.current = 0;
    }
  }, [tracker, loggedUser, company, device, events.pageLeft]);
  
  const trackPageViewed = useCallback((pageName: string) => {
    if (!pageName || pageName.trim() === '') {
      if (currentPageRef.current && timer.current > 0) {
        trackPageLeft(currentPageRef.current);
        timer.current = 0;
        currentPageRef.current = '';
      }
      return;
    }
    
    if (pageName === currentPageRef.current) {
      return;
    }
    
    if (currentPageRef.current && timer.current > 0) {
      trackPageLeft(currentPageRef.current);
    }
    
    timer.current = Date.now();
    currentPageRef.current = pageName;
    
    tracker?.track(events.pageViewed, {
      page_name: pageName,
      user_id: loggedUser?.id,
      user_name: loggedUser?.name,
      role: loggedUser?.role,
      client: company?.name,
      device: device,
      timestamp: new Date(timer.current).toISOString(),
    });
  }, [tracker, loggedUser, company, device, events.pageViewed, trackPageLeft]);
  
  const trackPage = useCallback((pageName: string) => {
    trackPageViewed(pageName);
  }, [trackPageViewed]);
  
  useEffect(() => {
    if (config.autoTrack !== false && config.initialPageName) {
      trackPageViewed(config.initialPageName);
    }
    
    // Handle beforeunload for navigation between MFEs
    const handleBeforeUnload = () => {
      if (currentPageRef.current) {
        trackPageLeft(currentPageRef.current);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (currentPageRef.current) {
        trackPageLeft(currentPageRef.current);
      }
    };
  }, []);
  
  return {
    trackPage,
    currentPage: currentPageRef.current,
    isTracking: timer.current > 0
  };
};
